import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, Row, Col, Divider, DatePicker, notification } from 'antd';
import {
  Button,
  DropdownButton,
  TextArea,
} from '@prio365/prio365-react-library';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { makePrioStyles } from '../../../theme/utils';
import useDatePickerLocale from '../../../hooks/useDatePickerLocale';

import ProjectPicker from '../../projects/components/ProjectPicker';
import {
  Task,
  CreateTaskRequest,
  emptyCreateTaskRequest,
  TaskFormModel,
} from '../../../models/Task';
import { ProjectId } from '../../../models/Types';
import ContactPicker from '../../contacts/components/ContactPicker';
import { PickerLocale } from 'antd/lib/date-picker/generatePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInternalProjectContacts } from '../../projects/actions';
import { getActiveProject } from '../../../apps/main/rootReducer';
import { rowGutter, colon } from '../../../util/forms';
import { Project } from '../../../models/Project';
import BucketsSelector from './BucketsSelector';
import classNames from 'classnames';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import { apiCreateFastCompletion } from '../api';
import {
  TASK_GENERATION_PROMPTS,
  buildTaskGenerationPrompt,
} from '../config/taskAIPrompts';

const useStyles = makePrioStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
  },
  submitButtonFormItem: {
    textAlign: 'right',
    marginBottom: theme.old.spacing.unit(2),
  },
  actionButtonsRow: {
    marginTop: theme.old.spacing.unit(2),
    marginBottom: theme.old.spacing.unit(2),
  },
  datePicker: {
    width: '100%',
  },
  timePicker: {
    width: `calc(75% - ${16 + theme.old.spacing.unit(1)}px)`,
    marginRight: theme.old.spacing.unit(1),
  },
  duration: {
    width: `calc(25% - ${theme.old.spacing.unit(1)}px)`,
    marginRight: theme.old.spacing.unit(1),
  },
  space: {
    display: 'flex',
  },
  periodSum: {
    textAlign: 'right',
  },
  mutedColor: {
    color: theme.old.typography.colors.muted,
  },
  deleteButton: {
    float: 'left',
  },
  scrollableFormModal: {
    overflowY: 'auto',
    overflowX: 'hidden',
    height: 'calc(100% - 48px)',
  },
  scrollableFormWidget: {
    overflowY: 'auto',
    overflowX: 'hidden',
    height: 'calc(100% - 48px - 32px)',
  },
  aiSection: {
    backgroundColor: '#fafafa',
    padding: theme.old.spacing.unit(2),
    borderRadius: theme.old.borders.radius,
    marginBottom: theme.old.spacing.unit(2),
    border: `1px solid #e0e0e0`,
  },
  aiSectionTitle: {
    fontWeight: 500,
    marginBottom: theme.old.spacing.unit(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.old.spacing.unit(1),
  },
}));

const CountedInput: React.FC<
  React.ComponentProps<typeof Input> & { value?: string }
> = ({ value = '', ...props }) => (
  <Input
    {...props}
    value={value}
    suffix={
      <span
        style={{
          fontSize: 12,
          color: value.length >= (props.maxLength ?? 100) ? '#ff4d4f' : '#00000073',
        }}
      >
        {value.length}/{props.maxLength ?? 100}
      </span>
    }
  />
);

interface TaskFormProps {
  className?: string;
  initialValues?: Task;
  disableActionButton?: boolean;
  disableForm?: boolean;
  actionLabel: string;
  cancelLabel?: string;
  onFinish: (value: CreateTaskRequest | Task, shouldOpen?: boolean) => void;
  onCancel?: () => void;
  resetOnFinish?: boolean;
  isDrawer?: boolean;
}

const convertToModel: (
  initialValue: Task | CreateTaskRequest
) => TaskFormModel = (initialValue: Task | CreateTaskRequest) => ({
  ...initialValue,
  dueDateTime:
    initialValue.dueDateTime &&
    moment(initialValue.dueDateTime, moment.ISO_8601),
  startDateTime:
    initialValue.dueDateTime &&
    moment(initialValue.dueDateTime, moment.ISO_8601),
});

const projectFilter: (project: Project) => boolean = (project) =>
  !!project.masterPlanId;

export const TaskForm: React.FC<TaskFormProps> = (props) => {
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const {
    className,
    initialValues,
    actionLabel,
    cancelLabel,
    onFinish,
    onCancel,
    disableActionButton,
    disableForm,
    resetOnFinish,
    isDrawer,
  } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const activeProject = useSelector(getActiveProject);
  const [currentProject, setCurrentProject] = useState<ProjectId>(
    initialValues?.projectId ?? activeProject
  );
  const [aiInput, setAiInput] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [saveAndOpen, setSaveAndOpen] = useState<boolean>(false);

  const dispatch = useDispatch();
  useEffect(() => {
    if (currentProject) dispatch(fetchInternalProjectContacts(currentProject));
  }, [currentProject, dispatch]);

  useEffect(() => {
    if (activeProject && !initialValues) {
      form.setFieldsValue({ projectId: activeProject, contactId: null });
      setCurrentProject(activeProject);
    }
  }, [activeProject, form, initialValues]);

  const convertedInitialValue = useMemo(
    () =>
      convertToModel(
        initialValues ?? {
          ...emptyCreateTaskRequest,
          projectId: currentProject ?? activeProject,
        }
      ),
    [initialValues, currentProject, activeProject]
  );

  const datePickerLocale: PickerLocale = useDatePickerLocale();

  useEffect(() => {
    form.resetFields();
  }, [initialValues, form]);

  const handleAIGenerate = async () => {
    if (!aiInput.trim()) {
      notification.warning({
        message: t('tasks:form.ai.emptyInput') || 'Eingabe erforderlich',
        description:
          t('tasks:form.ai.emptyInputDescription') ||
          'Bitte geben Sie eine Beschreibung ein, um die Aufgabe zu generieren.',
      });
      return;
    }

    setIsGenerating(true);
    try {
      // Get current form values for context
      const currentValues = form.getFieldsValue();
      const existingTitle = currentValues.title || '';
      const existingDescription = currentValues.description || '';

      // Build context string
      let existingContext = '';
      if (existingTitle || existingDescription) {
        existingContext = 'Bestehender Kontext aus dem Formular:\n';
        if (existingTitle) existingContext += `Titel: ${existingTitle}\n`;
        if (existingDescription)
          existingContext += `Beschreibung: ${existingDescription}\n`;
      }

      const userPrompt = buildTaskGenerationPrompt({
        userInput: existingContext ? `${existingContext}\n${aiInput}` : aiInput,
        // TODO: Add email context if available from props
        // emailSubject: props.emailSubject,
        // emailFrom: props.emailFrom,
        // emailBody: props.emailBody,
      });

      const { result, data } = await apiCreateFastCompletion({
        prompts: [
          {
            message: TASK_GENERATION_PROMPTS.system,
            promptType: 'system',
          },
          {
            message: userPrompt,
            promptType: 'user',
          },
        ],
      });

      if (result.status >= 200 && result.status < 300 && data) {
        // Extract completion string from response
        let completionText: string;

        // API returns {completion: string}
        if (typeof data === 'object' && data !== null && 'completion' in data) {
          completionText = data.completion;
        } else if (typeof data === 'string') {
          completionText = data;
        } else {
          completionText = JSON.stringify(data);
        }

        // Remove markdown code blocks if present (```json ... ```)
        const jsonMatch = completionText.match(
          /```(?:json)?\s*([\s\S]*?)\s*```/
        );
        const jsonString = jsonMatch
          ? jsonMatch[1].trim()
          : completionText.trim();

        // Parse the clean JSON
        const taskData = JSON.parse(jsonString);

        // Truncate checklist item titles to 100 characters (Planner API limit)
        const checklistItems = (taskData.checklistItems || []).map(
          (item: { title: string; isChecked: boolean }) => ({
            ...item,
            title: item.title?.substring(0, 100) || '',
          })
        );

        // Populate form fields
        form.setFieldsValue({
          title: taskData.title || '',
          description: taskData.description || '',
          checklistItems,
        });

        notification.success({
          message:
            t('tasks:form.ai.success') || 'Aufgabe erfolgreich generiert',
          description:
            t('tasks:form.ai.successDescription') ||
            'Die Aufgabe wurde automatisch ausgefüllt. Sie können die Felder jetzt nach Bedarf anpassen.',
        });
      } else {
        throw new Error('API request failed');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      notification.error({
        message: t('tasks:form.ai.error') || 'Generierung fehlgeschlagen',
        description:
          t('tasks:form.ai.errorDescription') ||
          'Die automatische Generierung ist fehlgeschlagen. Bitte versuchen Sie es erneut oder füllen Sie das Formular manuell aus.',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const cancel = () => {
    form.resetFields();
    setCurrentProject(activeProject);
    if (onCancel) {
      onCancel();
    }
  };

  const handleFinish: (value: TaskFormModel) => void = (
    value: TaskFormModel
  ) => {
    const {
      startDateTime: startDateTimeMoment,
      dueDateTime: dueDateTimeMoment,
      ...otherValues
    } = value;
    const startDateTime = startDateTimeMoment?.toISOString();
    const dueDateTime = dueDateTimeMoment?.toISOString();
    onFinish(
      {
        ...otherValues,
        startDateTime,
        dueDateTime,
      },
      saveAndOpen
    );
    if (resetOnFinish) {
      form.resetFields();
      form.setFieldsValue({
        projectId: currentProject,
      });
    }
    setSaveAndOpen(false);
  };

  const now = moment();

  return (
    <Form
      className={classNames(classes.root, className)}
      initialValues={convertedInitialValue}
      form={form}
      onValuesChange={(changedValues: TaskFormModel) => {
        if (changedValues.projectId) {
          setCurrentProject(changedValues.projectId);
          form.setFieldsValue({ internalProjectContactId: null });
        }
      }}
      onFinish={handleFinish}
      layout="vertical"
      onKeyDown={(e) => {
        if (
          e.key === 'Enter' &&
          (e.target as HTMLElement).tagName !== 'TEXTAREA'
        ) {
          e.preventDefault();
        }
      }}
    >
      <div
        className={
          isDrawer ? classes.scrollableFormModal : classes.scrollableFormWidget
        }
      >
        <div className={classes.aiSection}>
          <div className={classes.aiSectionTitle}>
            {t('tasks:form.ai.title') || 'Prio-Buddy'}
          </div>
          <TextArea
            placeholder={
              t('tasks:form.ai.placeholder') ||
              'Beschreiben Sie die Aufgabe... (z.B. "Vorbereitung Kundenpräsentation für morgen")'
            }
            value={aiInput}
            onChange={(value) => setAiInput(value)}
            disabled={disableForm || isGenerating}
            rows={3}
          />
          <Row justify="end" style={{ marginTop: theme.old.spacing.unit(1) }}>
            <Col>
              <Button
                type="default"
                htmlType="button"
                onClick={handleAIGenerate}
                disabled={disableForm || isGenerating || !aiInput.trim()}
                loading={isGenerating}
              >
                {t('tasks:form.ai.generate') || 'Generieren'}
              </Button>
            </Col>
          </Row>
        </div>
        <Row gutter={theme.old.spacing.unit(rowGutter)}>
          <Col span={24}>
            <Form.Item
              name="projectId"
              label={t('tasks:form.labels.projectId')}
              rules={[
                {
                  required: true,
                  message: t('tasks:form.validation.missing.projectId'),
                },
              ]}
            >
              <ProjectPicker disabled={disableForm} filter={projectFilter} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={theme.old.spacing.unit(rowGutter)}>
          <Col span={24}>
            <Form.Item
              name="title"
              colon={colon}
              label={t('tasks:form.labels.title')}
              rules={[
                {
                  required: true,
                  message: t('tasks:form.validation.missing.title'),
                },
              ]}
            >
              <Input disabled={disableForm} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={theme.old.spacing.unit(rowGutter)}>
          <Col span={24}>
            <Form.Item
              name="description"
              label={t('tasks:form.labels.description')}
              colon={colon}
            >
              <TextArea disabled={disableForm} rows={8} charLimit={4000} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={theme.old.spacing.unit(rowGutter)}>
          <Col span={24}>
            <Form.List name="checklistItems">
              {(fields, { add, remove }) => (
                <>
                  <div style={{ marginBottom: theme.old.spacing.unit(1) }}>
                    <label style={{ fontWeight: 500 }}>
                      {t('tasks:form.labels.checklist') || 'Checkliste'}
                    </label>
                  </div>
                  {fields.map((field) => (
                    <Row
                      key={field.key}
                      gutter={theme.old.spacing.unit(rowGutter)}
                      align="middle"
                    >
                      <Col span={20}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'title']}
                          fieldKey={[field.fieldKey, 'title']}
                          rules={[
                            {
                              max: 100,
                              message:
                                t(
                                  'tasks:form.validation.maxLength.checklistItem'
                                ) ||
                                'Checklisteneintrag darf maximal 100 Zeichen lang sein.',
                            },
                          ]}
                        >
                          <CountedInput
                            maxLength={100}
                            disabled={disableForm}
                            placeholder={
                              t('tasks:form.placeholders.checklistItem') ||
                              'Eintrag'
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        span={4}
                        style={{ paddingBottom: theme.old.spacing.unit(2) }}
                      >
                        <Button
                          type="link"
                          onClick={() => remove(field.name)}
                          disabled={disableForm}
                          size="default"
                          iconProp={['fal', 'trash-alt']}
                        ></Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button
                      type="default"
                      htmlType="button"
                      onClick={() => add({ title: '', isChecked: false })}
                      disabled={disableForm}
                      iconProp={['fal', 'plus']}
                      size="small"
                    >
                      {t('tasks:form.actions.addChecklistItem') ||
                        'Eintrag hinzufügen'}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Col>
        </Row>
        <Row gutter={theme.old.spacing.unit(rowGutter)}>
          <Col span={24}>
            <Form.Item
              name="internalProjectContactIds"
              label={
                t('tasks:form.labels.internalProjectContactIds') ||
                'Interne Kontakte'
              }
            >
              <ContactPicker
                onlyInternalProject
                projectId={currentProject}
                multiple
                disabled={disableForm || !currentProject}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={theme.old.spacing.unit(rowGutter)}>
          <Col span={24}>
            <Form.Item name="bucketId" label={t('tasks:form.labels.bucket')}>
              <BucketsSelector
                projectId={currentProject}
                disabled={disableForm || !currentProject}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Divider />
          </Col>
        </Row>
        <Row gutter={theme.old.spacing.unit(rowGutter)}>
          <Col span={12}>
            <Form.Item
              name="startDateTime"
              label={t('tasks:form.labels.startDateTime')}
              colon={colon}
            >
              <DatePicker
                disabled={disableForm}
                locale={datePickerLocale}
                format="DD.MM.YYYY"
                className={classes.datePicker}
                suffixIcon={<FontAwesomeIcon icon={['fal', 'calendar-alt']} />}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="dueDateTime"
              label={t('tasks:form.labels.dueDateTime')}
              colon={colon}
            >
              <DatePicker
                disabled={disableForm}
                locale={datePickerLocale}
                format="DD.MM.YYYY"
                className={classes.datePicker}
                suffixIcon={<FontAwesomeIcon icon={['fal', 'calendar-alt']} />}
                disabledDate={(date) => date.isBefore(now)}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
      <Row justify="end" className={classes.actionButtonsRow}>
        <Col span={24}>
          <Form.Item className={classes.submitButtonFormItem}>
            <Button type="default" htmlType="button" onClick={cancel}>
              {cancelLabel}
            </Button>

            <DropdownButton
              htmlType="submit"
              disabled={disableActionButton}
              style={{ marginInline: '8px' }}
              dropdownOptions={[
                {
                  value: 'saveAndOpen',
                  type: 'default',
                  label: t('tasks:form.saveAndOpen') || 'Speichern & Öffnen',
                  iconProp: ['fal', 'external-link'],
                  onClick: () => {
                    setSaveAndOpen(true);
                    form.submit();
                  },
                  disabled: disableActionButton,
                },
              ]}
            >
              {actionLabel}
            </DropdownButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default TaskForm;
